import { useMutation, type UseMutationResult } from "@tanstack/react-query";
import { useApi } from "@platform/api";

import type { AxiosInstance } from "axios";

export interface CarrierNoteParam {
  carrierName: string;
  notes: string;
}

export interface ExcelAppendFileParam {
  fileName: string;
  fileBytes: string;
}

// One requested tab position. isFromWorkbook disambiguates a title that
// collides between the presentation's own generated/template sheets and an
// appended workbook's sheets — Chameleon renames the losing side of such a
// collision on append, so two entries can share the exact same title with no
// other way to tell which physical sheet each one means.
export interface SheetOrderEntryParam {
  title: string;
  isFromWorkbook: boolean;
}

export interface GenerateMarketingPresentationParams {
  rfpId: string;
  templatePath: string;
  sheetOrder: SheetOrderEntryParam[];
  // The analyst's Present-tab carrier sequence, so the exported sheets keep
  // the on-screen carrier order; omitted when no custom order was applied.
  carrierOrder?: string[];
  // Name the analyst configured in the drawer. Names the workbook stored in
  // SharePoint; omitted when the drawer has no name to send, leaving the BFF's
  // default.
  fileName?: string;
  carrierNotes?: CarrierNoteParam[];
  excelAppendFile?: ExcelAppendFileParam;
}

// The BFF serves two contracts on this endpoint (ADR-028): the legacy path
// streams the xlsx bytes for the browser to download, while the v2 path (gated
// server-side) uploads to SharePoint and returns `{ fileName, presentationUrl }`
// JSON. The response content type is the only signal for which one we got.
export type GenerateMarketingPresentationResult =
  | { kind: "download"; fileName: string; blob: Blob }
  | { kind: "link"; fileName: string; presentationUrl: string };

const isPresentationLink = (
  value: unknown,
): value is { fileName: string; presentationUrl: string } =>
  typeof value === "object" &&
  value !== null &&
  typeof (value as Record<string, unknown>).fileName === "string" &&
  typeof (value as Record<string, unknown>).presentationUrl === "string";

const generateMarketingPresentation = async (
  axiosInstance: AxiosInstance,
  params: GenerateMarketingPresentationParams,
): Promise<GenerateMarketingPresentationResult> => {
  const response = await axiosInstance.post<Blob>(
    `gateway/rfps/${encodeURIComponent(params.rfpId)}/marketing-presentation`,
    {
      templatePath: params.templatePath,
      sheetOrder: params.sheetOrder,
      carrierOrder: params.carrierOrder,
      fileName: params.fileName,
      carrierNotes: params.carrierNotes,
      excelAppendFile: params.excelAppendFile,
    },
    { responseType: "blob" },
  );

  // A JSON body arrives as a Blob under `responseType: "blob"`, so read it back
  // as text to recover the SharePoint link.
  const contentType = (response.headers["content-type"] as string) ?? "";
  if (contentType.includes("application/json")) {
    const parsed: unknown = JSON.parse(await response.data.text());
    if (!isPresentationLink(parsed)) {
      throw new Error(
        "Marketing presentation response did not match the expected { fileName, presentationUrl } shape",
      );
    }
    return { kind: "link", ...parsed };
  }

  const contentDisposition =
    (response.headers["content-disposition"] as string) ?? "";
  const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
  const fileName = fileNameMatch?.[1] ?? "marketing-presentation.xlsx";

  return { kind: "download", fileName, blob: response.data };
};

const useGenerateMarketingPresentation = (): UseMutationResult<
  GenerateMarketingPresentationResult,
  Error,
  GenerateMarketingPresentationParams
> => {
  const axiosInstance = useApi();
  return useMutation<
    GenerateMarketingPresentationResult,
    Error,
    GenerateMarketingPresentationParams
  >({
    mutationFn: (params) =>
      generateMarketingPresentation(axiosInstance, params),
  });
};

export default useGenerateMarketingPresentation;
